/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// SiteTicker — full-bleed promo bar that sits ABOVE <header> in the DOM.
//
// Legacy notes (ecmSiteTickerItems.js):
// - One slide at a time, opacity fade transition
// - 6000ms auto-advance, pause on hover, resume on mouse-leave
// - Homepage-NMG delays start 2s after window load
// - BGO CMS version uses react-slick with arrows:true
// - Live NM production shows visible prev/next chevrons
//
// Implementation decisions vs legacy:
// - No react-slick dependency — plain useState + CSS opacity transition
// - Messages are props (wired to a site preference or Page Designer slot later)
// - No auto-advance delay on mount — 6s interval starts immediately
//
// ARROWS AT MOBILE. The line that used to sit here read "Arrows visible on
// desktop; hidden on mobile (matches NM live production)". That claim is
// DISPROVEN and is corrected rather than deleted, because it is the reason the
// arrows were hidden in the first place.
//
// RE-SOURCED 2026-08-19. This block used to cite re-rendered saved pages under
// `.reference/dumps/` for both brands. That source is barred. NM's numbers turn
// out to be reproducible twice over without it, and are kept; BG's are not, and
// are demoted below.
//   - NM, from `.reference/captures/search-m-390/App.tsx:21-23,59-60`: the
//     track is `w-[83%] max-w-[94%] px-5` and each arrow is a `w-5 h-5`
//     (= 20x20) button at `left-[-25px]` / `right-[-25px]`, centred with
//     `top-[50%] translate-y-[-50%]`. 83% of 390 = 323.7 centred → track
//     33.15..356.85, so the arrows resolve to x=8.15 and x=361.85 — live's
//     20x20 at 8px inside each edge, derived from the capture alone.
//   - NM, from the owner's `.reference/live-shots/nm-search-390.png` (sRGB,
//     2.0 px/CSS px): white chevron ink at x=12.0..19.5 and x=370.0..377.5
//     inside the black band. The capture's arrow art is an inline base64 SVG
//     whose stroked path spans 4.25..11.58 of its 20-box on the left and
//     8.42..15.75 on the right; placed at 8 and 362 that predicts ink at
//     12.25..19.58 and 370.42..377.75. The painted pixels land on it.
//     Presence, box and inset all confirmed without the dump.
//   - CHEVRON OPACITY IS 1, and the `opacity-[0.75]` that appears beside these
//     arrows in the capture is a decoy: it sits on an empty `content-[""]`
//     `text-[0px]` slick pseudo-element carrier, not on the painted glyph,
//     which is the button's own background-image. The owner's pixels agree —
//     the chevron ink is pure rgb(255,255,255) on black. Do not apply 0.75.
//   - BG — RECOVERED 2026-08-19, and this line used to say UNMEASURED. The
//     16x16-at-0-inset geometry does NOT come only from the barred dump: it
//     is in the CLASS channel of FIVE non-dump captures, byte-identical —
//     bg-home-m-375, bg-home-m-440, bg-search-m-375, bg-search-m-440 and
//     bg-plp-m-440 all carry, once per edge:
//       className="bg-center bg-no-repeat bg-contain absolute text-center
//                  w-4 h-4 left-0 top-[50%] …"   (and the `right-0` twin)
//     `w-4 h-4` IS the 16x16 box and `left-0`/`right-0` IS the 0px inset.
//     tokens/brand-bg.css already re-derived the INSET from these same five
//     files; this comment contradicted that file for no reason. The BOX is
//     measured on the same evidence line as the inset — one grep, one result.
//     (The element is a <button>, not a div.)
//
//     THE ARTWORK IS RECOVERED TOO, from the ASSET channel — the glyph is not
//     an <img> but this button's own `backgroundImage`, and it is an inline
//     base64 SVG, so it decodes offline (bg-home-m-375:18):
//       viewBox="0 0 307.2 512"
//       left   d="M307.2 31 274.1 0 0 256l274.1 256 33.1-30.9L66.4 256z"
//       right  d="m0 481 33.1 31 274.1-256L33.1 0 0 30.9 240.8 256z"
//       fill="#CCC"  on both
//     Two things follow. The glyph is 0.6:1, so under `bg-contain` it paints
//     9.6x16 inside the 16px box — it is NOT a square chevron. And BG's ink is
//     #CCC, a light grey, where NM's is pure rgb(255,255,255) (confirmed off
//     the owner's pixels above). That is a real brand divergence, and it was
//     in the captures the whole time.
//
//     Channels searched: className (hit — box + inset), style={{…}} (hit —
//     backgroundImage plus a fontFamily:"Arial"; no geometry), asset channel
//     (hit — base64, decoded above).
//
// APPLIED 2026-08-19 — see TICKER_ARROW_BY_BRAND in `./arrow-art.ts`. Every line
// above this one is the evidence; what follows is why the box could finally land.
//
// THE BOX AND THE GLYPH LAND TOGETHER, AND THAT ORDER WAS THE WHOLE PROBLEM.
// A prior pass correctly DECLINED shrinking BG's box from 20 to 16 on its own:
// our 20-box holds a STROKED chevron whose ink is 4.25..11.58 (path x 5..10.833
// plus half of the 1.5 stroke at each end), so at inset 0 the ink centre is
// 7.92; putting that same glyph in a 16-box would centre it at ~5.92 and move
// AWAY from live's ~8. That decline was right, and it is not reversed here —
// it is DISCHARGED, because the glyph half is now in hand. Live's own
// mechanism is `bg-contain` on a 0.6-aspect artwork in a 16x16 box, so the
// painted glyph is 9.6 x 16 CENTRED in that box: 3.2..12.8, centre 8.0.
// Measurement-discipline rule 3 — a box applies as a box model, with its
// contents — is satisfied for the first time.
//
// VERIFIED ON OUR OWN APP AT A PINNED 390 (clientWidth 390, data-brand=bg),
// two instruments, both reported because they disagree in the last decimal and
// only one of them is exact:
//   geometry (getBoundingClientRect on the painted node)
//     before  4.25..11.58  centre 7.92   (stroke path 5..10.833 ± 0.75)
//     after   3.20..12.79  centre 8.00   (svg rect x 3.2, w 9.59, h 16)
//   pixels (headless screenshot, deviceScaleFactor 4, non-black columns)
//     before  4.25..12.00  centre 8.13
//     after   3.00..12.50  centre 7.75
// The pixel instrument spreads ±1 device px each side on antialiased edges, so
// it brackets the geometry rather than contradicting it. THE INK CENTRE DID NOT
// MEANINGFULLY MOVE and was never the point — it was ~8 before and is 8.00 now,
// against live's ~8. What changed is everything else about the glyph: WIDTH
// 7.33 -> 9.60 (live's owner-pixel read is 9.4), HEIGHT 13.17 -> 16, and COLOUR
// rgb(255,255,255) -> rgb(204,204,204), the last confirmed off our own rendered
// pixels and not merely off the computed style.
//
// NM was measured in the same run and did not move by either instrument: box
// 20x20 at left 16, ink 20.25..28.00 pixel / centre 24.13, rgb(255,255,255) —
// byte-identical before and after, at 390, 1024 and 1280.
//
// The 16x16 box is not a mobile-only reading. `w-4 h-4` is byte-identical at
// EVERY captured BG width — bg-home-m-375/m-440, bg-search-m-375/m-440,
// bg-plp-m-440 (`left-0`), bg-home-d-1024 (`left-6`), bg-home-d-1280,
// bg-search-d-1280, bg-pdp-d-1440 (`left-[68px]`). The INSET steps across those
// tiers and is already tokenised as --ticker-arrow-inset; the BOX does not step,
// which is why it is one value in the artwork map rather than a token ladder.
//
// WHY THE BOX AND THE INK SIT WITH THE GLYPH AND NOT IN A TOKEN. They are not
// three values that happen to sit near each other — a 307.2x512 path only means
// anything at 9.6x16 filled in #CCC, and splitting them across a token file and
// a component is exactly the half-application this file has already been burned
// by twice. One arm, one citation, one place: `./arrow-art.ts`. The INSET stays
// a token because it genuinely varies per width and per brand and has nothing to
// do with the artwork.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useBrand } from '@/lib/brand/use-brand';
import { TICKER_ARROW_BY_BRAND, type TickerArrowArt } from './arrow-art';

export interface SiteTickerMessage {
    text: string;
    href?: string;
}

interface SiteTickerProps {
    messages: SiteTickerMessage[];
    /** Auto-advance interval in ms. Default: 6000 (matching legacy playSpeed default). */
    playSpeed?: number;
    className?: string;
    /**
     * Desktop band variant, stamped as `data-ticker-band` for the scoped rule
     * in src/theme/tokens/header.css. Owner, 2026-08-16: live drift 50 home / 44
     * elsewhere — matched per page for flicker parity on search. The layout
     * (the only place that knows the route) passes 'home' on the home route;
     * everything else keeps the 44px default. Mobile stays 60 everywhere.
     */
    band?: 'home' | 'default';
}

/**
 * `true` when the visitor has asked their OS for reduced motion.
 *
 * Starts `false` and is corrected in an effect rather than read during render:
 * `matchMedia` does not exist during SSR, and a render-phase read would make
 * the server and the first client render disagree. `false` is the right initial
 * value because it matches what the server renders for everyone.
 *
 * DUPLICATED, deliberately, in `components/home-content/hero.tsx`. The shared
 * home for this is `src/hooks/`, which this change does not own; twelve honest
 * lines in two files beats reaching outside the manifest, and promoting it is a
 * mechanical follow-up.
 */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion(): boolean {
    const [reduced, setReduced] = useState(false);

    useEffect(() => {
        const mql = globalThis.matchMedia?.(REDUCED_MOTION_QUERY);
        if (!mql) return;
        setReduced(mql.matches);
        const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
        mql.addEventListener?.('change', onChange);
        return () => mql.removeEventListener?.('change', onChange);
    }, []);

    return reduced;
}

/**
 * The glyph, sized from the box by the viewBox's own aspect — the inline-SVG
 * equivalent of live's `bg-contain`. Height fills the box; width follows the
 * ratio (NM 20x20 at 1:1, BG 9.6x16 at 0.6:1). Computed rather than left to the
 * browser's intrinsic-ratio sizing so the number is inspectable here.
 */
function TickerChevron({ art, side }: { art: TickerArrowArt; side: 'left' | 'right' }) {
    const [, , vbWidth, vbHeight] = art.viewBox.split(' ').map(Number);
    const height = art.boxSize;
    const width = +((height * vbWidth) / vbHeight).toFixed(3);
    // Named reads, not `art[side]`. `declared-never-read.test.ts` counts a
    // *_BY_BRAND field as read only when it sees a property ACCESS, so a
    // computed lookup makes both path arms invisible to it — the guard reported
    // `left, right` unread while they were painting on screen. A subscript is
    // shorter and buys a false negative in the one check whose whole job is to
    // find brand knobs nothing turns.
    const d = side === 'left' ? art.left : art.right;
    return (
        <svg
            width={width}
            height={height}
            viewBox={art.viewBox}
            fill="none"
            aria-hidden="true"
            data-testid={`site-ticker-arrow-${side}`}>
            <path
                d={d}
                {...(art.paint === 'fill'
                    ? { fill: 'currentColor' }
                    : {
                          stroke: 'currentColor',
                          strokeWidth: art.strokeWidth,
                          strokeLinecap: 'square' as const,
                          strokeLinejoin: 'round' as const,
                      })}
            />
        </svg>
    );
}

export default function SiteTicker({ messages, playSpeed = 6000, className, band = 'default' }: SiteTickerProps) {
    const { t } = useTranslation('header');
    // See the ARROWS AT MOBILE note at the top of this file. BOTH measured
    // brands paint prev/next at 390, so neither carries the sub-md `hidden` —
    // VISIBILITY is still a value both arms agree on, and a keyed switch whose
    // arms agree is a branch pretending to be a brand fact. A brand that
    // genuinely hides them declares that in HEADER_COMPOSITION alongside its
    // other structural gates, not here.
    const arrowVisibility = 'flex';
    // ARTWORK, on the other hand, genuinely forks — 20x20 stroked white for NM
    // against 9.6x16 filled #CCC for BG, from two evidence lines. That is what
    // `useBrand()` is back for; see TICKER_ARROW_BY_BRAND above.
    const brand = useBrand();
    const arrow = TICKER_ARROW_BY_BRAND[brand];
    // Only BG names an ink colour; NM inherits the band's --ticker-foreground,
    // so its computed `color` is byte-identical to what it was before the map.
    const arrowStyle = {
        width: arrow.boxSize,
        height: arrow.boxSize,
        ...(arrow.ink ? { color: arrow.ink } : {}),
    };
    const [index, setIndex] = useState(0);
    const [visible, setVisible] = useState(true);
    const pausedRef = useRef(false);
    // KEYBOARD PAUSE, alongside the hover pause `pausedRef` already carried.
    // A pointer user could always stop this band by resting the cursor on it;
    // a keyboard or screen-reader user had no way to stop it at all, and the
    // copy changed under them every `playSpeed` ms — WCAG 2.2.2 Level A.
    // Two refs rather than one because the two conditions are independent:
    // tabbing out of a band the pointer is still resting on must NOT restart it.
    const focusedRef = useRef(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const prefersReducedMotion = usePrefersReducedMotion();

    const advance = useCallback(
        (direction: 1 | -1) => {
            setVisible(false);
            setTimeout(() => {
                setIndex((prev) => (prev + direction + messages.length) % messages.length);
                setVisible(true);
            }, 200);
        },
        [messages.length]
    );

    useEffect(() => {
        if (messages.length <= 1) return;
        // Reduced motion means NO timer at all, not a slower one. The arrows
        // below stay — a user who asked for less motion still gets to read the
        // other slides, on their own trigger.
        if (prefersReducedMotion) return;
        intervalRef.current = setInterval(() => {
            if (!pausedRef.current && !focusedRef.current) advance(1);
        }, playSpeed);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [advance, playSpeed, messages.length, prefersReducedMotion]);

    if (!messages.length) return null;

    const current = messages[index];
    // Type role: `subheadingUppercaseEmphasis` (§2.2 — 16px/24px/700 + uppercase).
    // The ticker is display copy — a promotional statement, not a control label —
    // so a subheading role rather than a label role. It is also the one 16px
    // uppercase+bold role that holds constant across both breakpoint tiers, so
    // the bar's line box does not jump at the 1024px typography breakpoint.
    // 3c blast-1 (.reference/harvest/les-blast-1-header-1280.md "Ticker slide
    // text"): live ls is NORMAL where the role carries 0.16 ("ls normal vs
    // role 0.16") — a ticker-LOCAL override via --ticker-letter-spacing
    // (tokens/header.css); the subheading role token itself stays untouched.
    // SIZE AND LEADING JOIN THE ELEMENT-LOCAL HOP that letter-spacing already
    // used. `type-subheading` supplies all three properties from the shared
    // typography role, and BOTH brands' ticker copy diverges from that role on
    // this element: live NM steps the leading 16 / 40 / 30 with the viewport
    // while holding 16px type, live BG runs 13px/21px/0.16px flat. Neither is
    // expressible by moving the role tokens, which the PDP price and every other
    // subheading also read — so the divergence is scoped to the element that
    // actually measured it. The tokens' shared neutrals restate the role's own
    // values (tokens/header.css), so a brand that sets neither renders exactly
    // what it did before this line.
    const messageClass =
        'type-subheading text-(length:--ticker-font-size) leading-(--ticker-line-height) tracking-(--ticker-letter-spacing) font-bold uppercase text-center transition-opacity duration-200';

    return (
        <div
            role="region"
            aria-label={t('ticker.region')}
            data-ticker-band={band}
            className={cn('w-full bg-ticker-background text-ticker-foreground', className)}
            onMouseEnter={() => {
                pausedRef.current = true;
            }}
            onMouseLeave={() => {
                pausedRef.current = false;
            }}
            // React's onFocus/onBlur are delegated focusin/focusout, so these
            // fire for the prev/next buttons and any link inside a slide —
            // `:focus-within` semantics without a state update or a re-render.
            onFocus={() => {
                focusedRef.current = true;
            }}
            onBlur={() => {
                focusedRef.current = false;
            }}>
            {/* Band height. The note that used to sit here read "fixed heights at
                TWO levels, so a slide whose copy wraps can never grow the band and
                push the page down" (micro-harvest-header2.md §2). That is
                CORRECTED rather than deleted, because it is the reason the two
                fixed heights were written in the first place — and NM live does
                not behave that way. Live's mobile band is a 60px MIN-height its
                two-line copy overflows to 62; the clamp was ours, not live's.

                The classes below are unchanged and still carry the tokens.
                `theme/overrides/app-shell.css` relaxes them to
                `min-block-size` + padding for NM below `md` only, and holds the
                whole derivation (live's box model, the three agreeing sources for
                the 16px line pitch, and why BG — measured at 72, not 62 — is
                deliberately excluded). BG and every width above `md` still get
                exactly the fixed heights described above. */}
            <div
                data-testid="site-ticker-band"
                className="relative flex items-center justify-center px-10 h-(--ticker-height-mobile) md:h-(--ticker-height)">
                {messages.length > 1 && (
                    <button
                        onClick={() => advance(-1)}
                        aria-label={t('ticker.previous')}
                        style={arrowStyle}
                        className={cn(
                            arrowVisibility,
                            'absolute left-(--ticker-arrow-inset) items-center justify-center cursor-pointer hover:opacity-50 transition-opacity focus-ring-button'
                        )}>
                        <TickerChevron art={arrow} side="left" />
                    </button>
                )}

                {/* THE INNER WINDOW'S 36px FLOOR — live's `min-h-9`, and it is a
                    MIN-height, not the fixed height the harvest row claims.
                    micro-harvest-header2.md §2 reports the slick viewport as
                    "fixed height:36px; min-height:36px" read off computed style.
                    `height` in a computed style is the USED height, so 36 there
                    is what the content happened to occupy; the AUTHORED value is
                    visible in the capture and it is a minimum:

                      home-m-375/App.tsx:25   the viewport
                      home-m-390/App.tsx:25   same string at the second width
                        `overflow-hidden relative text-center w-full min-h-9`

                    `min-h-9` is 36px, and live carries it on the track (:21),
                    the viewport (:25), the rail (:26) and every slide (:27) —
                    with no `h-[36px]` anywhere in that subtree. Note the SURFACE:
                    search-m-390:21-27 is the same slider with NO min-height at
                    any level, so 36 is the home masthead's number and the floor
                    below is stated as live states it rather than as a clamp.

                    THIS DOES NOT MOVE THE BAND, by construction, and that is the
                    point rather than a disappointment. NM mobile's band is a
                    60px MINIMUM (theme/overrides/app-shell.css) with 10px of
                    padding either side, so a floored 36px window gives 56 — under
                    the 60 minimum, which still wins. Two-line copy is 42 and was
                    already over the floor. BG mobile's window is the fixed 48
                    below, and every width from `md` up takes `md:h-full` against
                    a 44/50/53px band. 36 is beneath all of them.

                    What it DOES do is stop the window collapsing onto short copy:
                    a one-line slide measured 26px tall here against live's 36. */}
                <div
                    data-testid="site-ticker-window"
                    className="flex h-12 min-h-9 md:h-full items-center justify-center overflow-hidden">
                    {current.href ? (
                        <a
                            href={current.href}
                            data-testid="site-ticker-message"
                            className={cn(
                                messageClass,
                                'hover:underline hover:decoration-brand-green focus-ring-button'
                            )}
                            style={{ opacity: visible ? 1 : 0 }}>
                            {current.text}
                        </a>
                    ) : (
                        <span
                            data-testid="site-ticker-message"
                            className={messageClass}
                            style={{ opacity: visible ? 1 : 0 }}>
                            {current.text}
                        </span>
                    )}
                </div>

                {messages.length > 1 && (
                    <button
                        onClick={() => advance(1)}
                        aria-label={t('ticker.next')}
                        style={arrowStyle}
                        className={cn(
                            arrowVisibility,
                            'absolute right-(--ticker-arrow-inset) items-center justify-center cursor-pointer hover:opacity-50 transition-opacity focus-ring-button'
                        )}>
                        <TickerChevron art={arrow} side="right" />
                    </button>
                )}
            </div>
        </div>
    );
}
